// Vendor Imports
import _ from 'lodash';
import isEqual from 'lodash/isEqual';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';

// Project Imports
import { FilterFooter } from '../FilterFooter';
import FilterHeader from '../FilterHeader';

import {
  ForgeCheckbox,
  ForgeDivider,
  ForgeDrawer,
  ForgeIcon,
  ForgeList,
  ForgeListItem,
  ForgeSelect,
  ForgeTextField,
  ForgeTooltip,
} from '@tylertech/forge-react';
import { type PicklistOption } from 'common/components/Picklist';
import { I18n } from 'common/i18n';
import { type FilterValue, OPERATOR } from '../SoqlFilter';
import { type FilterColumnsMap, type FilterEditorProps } from '../types';
import { useFocusFirstActionableElement } from './InputFocus';
import * as TextFilter from '../lib/Filters/TextFilter';
import * as BaseFilter from '../lib/Filters/BaseFilter';
import { getOperator, isEqualityFilter } from '../lib/Filters/index';

// Constants
const scope = 'shared.components.filter_bar.text_filter';
export const DEFAULT_FETCH_RESULTS_DEBOUNCE_WAIT_TIME = 400;
export const SHOW_RESULTS_COUNT = 20;
export const FILTER_OFFSET = { DEFAULT: 0, MAX: 20 };
const INFINITE_SCROLL_DATA_LOAD_OFFSET = 25;

const CheckboxOption = ({
  checked,
  onChange,
  option,
  testId,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  option: PicklistOption;
  testId: string;
}) => (
  <ForgeListItem value={option.value}>
    <ForgeCheckbox
      aria-label={option.title}
      checked={checked}
      data-testid={testId}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
      slot="start"
    />
    <span>{option.title}</span>
    <ForgeTooltip placement="bottom">{option.title}</ForgeTooltip>
  </ForgeListItem>
);

export interface TextFilterEditorProps extends FilterEditorProps {
  appliedFilter: TextFilter.TextSoqlFilter;
  columns: FilterColumnsMap;
}

/**
 * Create the "null" suggestion to allow filtering on empty values.
 */
const getNullOption = (): PicklistOption => ({
  title: I18n.t('no_value', { scope }),
  value: null,
});

export function TextFilterEditor(props: TextFilterEditorProps) {
  const { allFilters, allParameters, appliedFilter, columns, dataProvider, independentTextFilters } = props;
  const { isReadOnly, onRemove, onUpdate, showRemoveButtonInFooter } = props;

  /**
   * NOTE: Other filter editors store individual UI components' values in state
   * and then apply them to the config separately. Text filter applies the values
   * directly to the filter config, so we store the entire dirty filter in state to
   * keep rerenders the same.
   */
  const [dirtyFilter, setDirtyFilter] = useState<TextFilter.TextSoqlFilter>(() => _.cloneDeep(appliedFilter));
  const [topXOptions, setTopXOptions] = useState<PicklistOption[]>([]);
  const [picklistOffset, setPicklistOffset] = useState(FILTER_OFFSET.DEFAULT);
  const [prevAppliedFilter, setPrevAppliedFilter] = useState(appliedFilter);

  const textFilterRef = useFocusFirstActionableElement<HTMLDivElement>();
  const picklistContainerRef = useRef<HTMLDivElement>(null);

  if (!isEqual(appliedFilter, prevAppliedFilter)) {
    setPrevAppliedFilter(appliedFilter);
    setDirtyFilter(_.cloneDeep(appliedFilter));
  }

  /**
   * Top X options are set up once and placed into state. This gets the Top X items
   *  and inserts the (No Value) item at the top of the list.
   */
  const getTopXOptionsRespectingFilters = () => {
    const infiniteScroll = appliedFilter.columns.length === 1;

    TextFilter.getTopXOptionsRespectingFilters({
      filter: appliedFilter,
      allFilters,
      allParameters,
      offset: infiniteScroll ? picklistOffset : 0,
      dataProviderConfigs: dataProvider,
      independentFilters: independentTextFilters,
    })
      .then((result) => {
        // Check to make sure component is mounted
        if (!textFilterRef.current) {
          return;
        }

        const newTopXOptions: PicklistOption[] = (result.topXOptions ?? []).map((option) => ({
          title: option.value,
          value: option.value,
        }));

        if (!infiniteScroll || (infiniteScroll && picklistOffset === 0)) {
          newTopXOptions.unshift(getNullOption());
        }
        if (infiniteScroll && picklistOffset > 0) {
          newTopXOptions.unshift(...topXOptions);
        }

        setTopXOptions(newTopXOptions);
        setPicklistOffset(picklistOffset + SHOW_RESULTS_COUNT);
      })
      .catch((error) => {
        console.error(`Soql like top values failed for ${BaseFilter.getFilterName(dirtyFilter, columns)}:`);
        console.error(error);
      });
  };

  const getTopXOptionsRespectingFiltersRef = useRef(getTopXOptionsRespectingFilters);
  getTopXOptionsRespectingFiltersRef.current = getTopXOptionsRespectingFilters;

  useEffect(() => {
    getTopXOptionsRespectingFiltersRef.current();
  }, []);

  const handleScrollForPicklistRef = useRef<ReturnType<typeof _.debounce>>();
  if (!handleScrollForPicklistRef.current) {
    handleScrollForPicklistRef.current = _.debounce(() => {
      const picklistContainerScrollTop = picklistContainerRef?.current?.scrollTop ?? 0;
      const picklistContainerHeight = picklistContainerRef?.current?.clientHeight ?? 0;
      const picklistContainerScrollHeight = picklistContainerRef?.current?.scrollHeight ?? 0;
      const picklistContainerBottomPosition = picklistContainerScrollTop + picklistContainerHeight;

      if (
        picklistContainerBottomPosition + INFINITE_SCROLL_DATA_LOAD_OFFSET >
        picklistContainerScrollHeight
      ) {
        getTopXOptionsRespectingFiltersRef.current();
      }
    }, DEFAULT_FETCH_RESULTS_DEBOUNCE_WAIT_TIME);
  }
  const handleScrollForPicklist = handleScrollForPicklistRef.current;

  const getSuggestions = (searchTerm: string) => {
    if (_.isEmpty(searchTerm)) {
      return Promise.resolve({ results: [] });
    }

    return TextFilter.getSuggestions({
      filter: dirtyFilter,
      searchTerm,
      allFilters,
      allParameters,
      dataProviderConfigs: dataProvider,
    }).catch((error) => {
      console.error(`Soql like search failed for ${BaseFilter.getFilterName(dirtyFilter, columns)}:`);
      console.error(error);
    });
  };

  const updateSelectedValues = (nextSelectedValues: FilterValue[]) => {
    const updatedFilter = TextFilter.getEqualityTextFilter(dirtyFilter, {
      values: _.uniq(nextSelectedValues),
    });
    setDirtyFilter(_.cloneDeep(updatedFilter));
    return updatedFilter;
  };

  const onSelectOption = (option?: PicklistOption | null, checkedState: boolean = true) => {
    if (dirtyFilter.singleSelect) {
      onUpdate(updateSelectedValues([option?.value]));
    } else if (checkedState) {
      updateSelectedValues(_.union(TextFilter.getOperands(dirtyFilter), [option?.value]));
    } else {
      updateSelectedValues(_.without(TextFilter.getOperands(dirtyFilter), option?.value));
    }
  };

  const resetFilter = () => {
    onUpdate(BaseFilter.reset(dirtyFilter, columns));
  };

  const applyFilter = () => {
    onUpdate(dirtyFilter);
  };

  const isDirty = TextFilter.hasOperands(dirtyFilter) && !_.isEqual(dirtyFilter, appliedFilter);

  const renderHeader = () => {
    const filterName = BaseFilter.getFilterName(dirtyFilter, columns);

    const onDropdownChange = (event: CustomEvent) => {
      const updatedFilter: TextFilter.TextSoqlFilter = TextFilter.setOperator(dirtyFilter, event.detail);
      setDirtyFilter(updatedFilter);
    };

    const dropdown = !_.get(dirtyFilter, 'singleSelect', false) ? (
      <ForgeSelect
        onChange={onDropdownChange}
        options={[
          { label: I18n.t('is', { scope }), value: OPERATOR.EQUALS },
          { label: I18n.t('is_not', { scope }), value: OPERATOR.NOT_EQUAL },
          { label: I18n.t('starts_with', { scope }), value: OPERATOR.STARTS_WITH },
          { label: I18n.t('contains', { scope }), value: OPERATOR.CONTAINS },
          { label: I18n.t('does_not_contain', { scope }), value: OPERATOR.DOES_NOT_CONTAIN },
        ]}
        value={getOperator(dirtyFilter)}
        variant="plain"
        theme="primary"
        density="small"
        popoverIcon={false}
        className="filter-operator-dropdown"
        aria-label={filterName ?? ''}
      >
        <ForgeIcon slot="end" name="chevron_down" theme="primary" icon_type="button" />
      </ForgeSelect>
    ) : null;

    return <FilterHeader name={filterName}>{dropdown}</FilterHeader>;
  };

  const getAllSuggestions = async (filterText: string) => {
    const options = await getSuggestions(filterText);
    const newOptions: PicklistOption[] = (options?.results ?? []).map((option) => ({
      title: option.title,
      value: option.title,
    }));
    setTopXOptions(newOptions);
  };

  const onFilterList = (filterText: string) => {
    if (_.isEmpty(filterText)) {
      return;
    }
    getAllSuggestions(filterText);
  };

  const renderSearchTextbox = () => (
    <ForgeTextField variant="filled" density="small">
      <ForgeIcon name="search" slot="start" />
      <label htmlFor="search-text-filter-input">Search values</label>
      <input
        id="search-text-filter-input"
        type="text"
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onFilterList(event.target.value)}
      ></input>
    </ForgeTextField>
  );

  const renderSingleSelectItems = () => {
    const selectedValues = TextFilter.getOperands(dirtyFilter);

    return topXOptions.map((option) => (
      <ForgeListItem
        data-testid={`${option.title}-text-filter-option`}
        key={`option-${option.title}`}
        on-forge-list-item-select={() => onSelectOption(option)}
        selected={selectedValues.includes(option.value)}
        value={option.value}
      >
        <button>{option.title}</button>
        <ForgeTooltip placement="bottom">{option.title}</ForgeTooltip>
      </ForgeListItem>
    ));
  };

  const renderMultiSelectItems = () => {
    const selectedValues = TextFilter.getOperands(dirtyFilter);
    const options = _.filter(topXOptions, (option) => !_.includes(selectedValues, option.value));

    const selectedOptions = _.map(selectedValues, (selectedValue) => ({
      title: _.isNull(selectedValue) ? I18n.t('no_value', { scope }) : selectedValue,
      value: selectedValue,
    }));

    const selectedListItems = selectedOptions.map((option) => (
      <CheckboxOption
        key={`option-${option.title}`}
        checked={selectedValues.includes(option.value)}
        onChange={(checked) => onSelectOption(option, checked)}
        option={option}
        testId={`${option.title}-selected-text-filter-option`}
      />
    ));

    const optionListItems = options.map((option) => (
      <CheckboxOption
        key={`option-${option.title}`}
        checked={selectedValues.includes(option.value)}
        onChange={(checked) => onSelectOption(option, checked)}
        option={option}
        testId={`${option.title}-text-filter-option`}
      />
    ));

    const divider = selectedListItems.length > 0 ? [<ForgeDivider key="forge-divider"></ForgeDivider>] : [];

    return [...selectedListItems, ...divider, ...optionListItems];
  };

  const renderPicklist = () => {
    if (topXOptions.length === 0) {
      return;
    }

    const listItems = dirtyFilter.singleSelect ? renderSingleSelectItems() : renderMultiSelectItems();

    return (
      <div
        className="text-filter-list-container"
        onScroll={handleScrollForPicklist}
        ref={picklistContainerRef}
      >
        <ForgeDivider></ForgeDivider>
        <ForgeDrawer open>
          <ForgeList className="forge-typography--body2" dense aria-label={I18n.t('results', { scope })}>
            {listItems}
          </ForgeList>
        </ForgeDrawer>
        <ForgeDivider></ForgeDivider>
      </div>
    );
  };

  const renderTextBox = () => {
    const containsValue = (TextFilter.getOperands(dirtyFilter)[0] ?? '') as string;

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const updatedFilter = TextFilter.getContainsTextFilter(dirtyFilter, {
        operand: event.target?.value ?? '',
      });
      setDirtyFilter(_.cloneDeep(updatedFilter));
    };

    return (
      <ForgeTextField variant="filled" density="small">
        <input className="text-input" onChange={onChange} value={containsValue} />
      </ForgeTextField>
    );
  };

  let suggestionsAutocomplete;
  let picklist;
  let textBox;

  if (isEqualityFilter(dirtyFilter)) {
    suggestionsAutocomplete = renderSearchTextbox();
    picklist = renderPicklist();
  } else {
    suggestionsAutocomplete = null;
    picklist = null;
    textBox = renderTextBox();
  }

  return (
    <div className="filter-controls text-filter" ref={textFilterRef}>
      <div className="column-container">
        {renderHeader()}
        {suggestionsAutocomplete}
        {picklist}
        {textBox}
      </div>
      <FilterFooter
        disableApplyFilter={!isDirty}
        isDrilldown={dirtyFilter.isDrilldown}
        isReadOnly={isReadOnly}
        onClickApply={applyFilter}
        onClickRemove={onRemove}
        onClickReset={resetFilter}
        showApplyButton={!dirtyFilter.singleSelect}
        showRemoveButton={showRemoveButtonInFooter}
      />
    </div>
  );
}
